import * as _ from 'lodash';
import axios from 'axios';
import { from, Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';

import { PAGE_SIZE, PROJECT_OVERDUE_TIME } from 'src/consts';
import { IAttachment, IProject, IProjectReadMarks } from 'src/models';

import timeService from './time.service';

interface IKeywordLocation {  // 搜素关键字在被搜索字符串中的位置信息
  index: number;  // 位置
  word: string; // 关键字
}

// 查找关键在被搜索字符串中的位置信息
function findKeywordLocations(
  str = '', // 被搜索字符串
  keyword = ''  // 关键字
): IKeywordLocation[] {
  if (!str || !keyword) {
    return [];
  }
  const regExp = new RegExp(keyword, 'ig');
  const locations: IKeywordLocation[] = [];
  let arr;
  while ((arr = regExp.exec(str)) !== null) {
    locations.push({
      index: arr.index,
      word: arr[0],
    });
  }
  return locations;
}

// 标记搜索关键字
// （关键字搜索时不区分大小写，但是标记时应按原始字符串显示）
function markKeywordSync(
  str = '', // 被搜索字符串
  keyword = ''  // 关键字
): string {
  if (!str || !keyword) {
    return str;
  }

  const MARK_LEFT = '<mark>';
  const MARK_RIGHT = '</mark>';
  const markLeftLocations = findKeywordLocations(str, MARK_LEFT);
  const markRightLocations = findKeywordLocations(str, MARK_RIGHT);
  let keywordLocations = findKeywordLocations(str, keyword);  // 关键字位置信息

  // 关键字位置信息，移除位于MARK_LEFT、MARK_RIGHT中的
  keywordLocations = _.reject(keywordLocations, k => {
    return -1 !== _.findIndex(markLeftLocations, m => k.index - m.index >= 0 && k.index - m.index < MARK_LEFT.length)
      || -1 !== _.findIndex(markRightLocations, m => k.index - m.index >= 0 && k.index - m.index < MARK_RIGHT.length);
  });

  _.forEachRight(keywordLocations, (item) => {
    str = str.slice(0, item.index) + `${MARK_LEFT}${item.word}${MARK_RIGHT}` + str.slice(item.index + item.word.length);
  });
  return str;
}

export default {
  //#region audit 项目列表
  // 给 audit 项目列表分配 _sn 属性
  assignAuditProjectsSnSync(projects: IProject[]): IProject[] {
    projects = _.cloneDeep(projects);
    let newProjects: IProject[] = [];

    // 获取一级项目（并非真正的一级项目，只要通过parent_puid找不到父项目，就将其作为一级项目）
    const levelOneProjects: IProject[] = [];
    let snNumber = 0;
    _.forEach(projects, (project) => {
      if (-1 !== _.findIndex(projects, { puid: project.parent_puid })) {
        return;
      }
      const levelOneProject = _.cloneDeep(project);
      levelOneProject._sn = ++snNumber + '';
      levelOneProjects.push(levelOneProject);
    });
    newProjects = [...levelOneProjects];

    // 给子项目分配sn，并插入到新数组中
    function assignChildProjectsSn(currentProject: IProject, allProjects: IProject[]) {
      const children = _.filter(allProjects, { parent_puid: currentProject.puid });
      _.forEach(children, (child, index) => {
        child._sn = `${currentProject._sn}.${index + 1}`;
      });
      const indexOfCurrentProject = _.findIndex(newProjects, { puid: currentProject.puid });
      newProjects.splice(indexOfCurrentProject + 1, 0, ...children);
      _.forEach(children, (child) => {
        assignChildProjectsSn(child, allProjects);
      });
    }
    _.forEach(levelOneProjects, (levelOneProject) => {
      assignChildProjectsSn(levelOneProject, projects);
    });

    return newProjects;
  },

  // audit 项目列表， 由 is_need_receipt 生成 _is_need_receipt，用于 UI 展示
  unzipAuditProjectsIsNeedReceiptSync(projects: IProject[]): IProject[] {
    projects = _.cloneDeep(projects);
    _.forEach(projects, (project) => {
      // project._is_need_receipt = project.is_need_receipt ? 'true' : 'false';
      project._is_need_receipt = project.is_need_receipt;
    });
    return projects;
  },

  // audit 项目列表， 由 _is_need_receipt 生成 is_need_receipt，用于提交数据
  zipAuditProjectsIsNeedReceiptSync(projects: IProject[]): IProject[] {
    projects = _.cloneDeep(projects);
    _.forEach(projects, (project) => {
      // project.is_need_receipt = project._is_need_receipt === 'true';
      project.is_need_receipt = project._is_need_receipt;
    });
    return projects;
  },

  // audit 项目列表， 由 is_secret 生成 _is_secret，用于 UI 展示
  unzipAuditProjectsIsSecretSync(projects: IProject[]): IProject[] {
    projects = _.cloneDeep(projects);
    _.forEach(projects, (project) => {
      project._is_secret = project.is_secret ? 'true' : 'false';
    });
    return projects;
  },

  // audit 项目列表， 由 _is_secret 生成 is_secret，用于提交数据
  zipAuditProjectsIsSecretSync(projects: IProject[]): IProject[] {
    projects = _.cloneDeep(projects);
    _.forEach(projects, (project) => {
      project.is_secret = project._is_secret === 'true';
    });
    return projects;
  },

  // audit 项目列表， 由 end_on、final_end_on 生成 _end_on，用于 UI 展示
  // _end_on：项目下发单、变更单，用于展示完成时间（主要用来处理延期的项目的完成时间显示）
  unzipAuditProjectsEndOnSync(projects: IProject[]): IProject[] {
    projects = _.cloneDeep(projects);
    _.forEach(projects, (project) => {
      project._end_on = project.final_end_on || project.end_on;
    });
    return projects;
  },

  // audit 项目列表， 由 _end_on 生成 end_on，用于提交数据
  zipAuditProjectsEndOnSync(projects: IProject[]): IProject[] {
    projects = _.cloneDeep(projects);
    _.forEach(projects, (project) => {
      project.end_on = project._end_on;
    });
    return projects;
  },

  // audit 项目列表，由audit.attachments.items中 过滤出 项目私有附件 project._attachments，用于 Project 组件编辑私有附件
  unzipAuditProjectsAttachmentsSync(
    projects: IProject[],
    attachments: IAttachment[] // audit.attachments
  ): IProject[] {
    projects = _.cloneDeep(projects);
    _.forEach(projects, (project) => {
      project._attachments = _.filter(attachments, { puid: project.puid });
    });
    return projects;
  },

  // audit，将项目私有附件 project._attachments 组合到 audit.attachments.items
  zipAuditProjectsAttachmentsSync(
    projects: IProject[],
    attachments: IAttachment[] // audit.attachments
  ): IAttachment[] {
    projects = _.cloneDeep(projects);
    attachments = _.cloneDeep(attachments);
    _.forEach(projects, (project) => {
      if (!project._attachments?.length) {
        return;
      }
      // 改项目的私有附件分配puid（取项目的puid）
      _.forEach(project._attachments, (attachment) => {
        attachment.puid = project.puid;
      });
      attachments = _.concat(attachments, project._attachments);
    });
    return attachments;
  },
  //#endregion

  //#region 项目中心-项目列表
  // 给 项目中心-项目列表 分配sn
  assignProjectsSnSync(projects: IProject[], page = 1, limit = PAGE_SIZE): void {
    _.forEach(projects, (item, index) => {
      const sn = (page - 1) * limit + index + 1;
      item._sn = sn.toString();
    });
  },

  //分配已读标记（new标记）
  assignProjectReadMarksSync(projects: IProject[]): void {
    const today = new Date();
    const projectReadMarks: IProjectReadMarks =
      JSON.parse(localStorage.getItem('projectReadMarks')) || {};
    for (const readProject in projectReadMarks) {
      if (
        today.getTime() - timeService.utc2gmt(projectReadMarks[readProject]).getTime() >
        PROJECT_OVERDUE_TIME
      ) {
        delete projectReadMarks[readProject];
      }
    }
    localStorage.setItem('projectReadMarks', JSON.stringify(projectReadMarks));
    _.forEach(projects, (project) => {
      // project._is_read = projectReadMarks.hasOwnProperty(project.id)
      project._is_read =
        project.id in projectReadMarks ||
        !project.down_time ||
        today.getTime() - timeService.utc2gmt(project.down_time).getTime() >
        PROJECT_OVERDUE_TIME;
    });
  },
  //#endregion

  //#region 分配需要高亮关键字的属性（将title转换成标记搜索关键字的html）
  // （title、person_liable、assigner、created_name、manager）
  assignProjectMarkedPropertiesSync(projects: IProject[], keywords: string[]): void {
    _.forEach(projects, (project) => {
      project._marked_title_html = project.title || '';
      project._person_liable_html = project.person_liable || '';
      project._assigner_html = project.assigner || '';
      project._created_name_html = project.created_name || '';
      project._manager_html = project.manager || '';
    });

    if (!keywords.length) {
      return;
    }
    _.forEach(projects, (project) => {
      // 若projectId不在matched_project_ids内，不需高亮
      if (!_.includes(project.matched_project_ids, project.id)) {
        return;
      }
      _.forEach(keywords, (keyword) => {
        project._marked_title_html = markKeywordSync(project._marked_title_html, keyword);
        project._person_liable_html = markKeywordSync(project._person_liable_html, keyword);
        project._assigner_html = markKeywordSync(project._assigner_html, keyword);
        project._created_name_html = markKeywordSync(project._created_name_html, keyword);
        project._manager_html = markKeywordSync(project._manager_html, keyword);
      });
    });
  },
  //#endregion

  //#region 分词
  cutKeywords$: undefined,

    /* private */ requestCutKeywordsObservable(
    q: string,
    enforceNew?: boolean // 强制获取最新数据，不取缓存
  ): Observable<string[]> {
    if (enforceNew || !this.cutKeywords$) {
      const params = { keywords: q };
      this.cutKeywords$ = from(
        // Observable -> Promise
        axios.get<string[]>(`/api/search/cut-keywords`, { params }).then((res) => {
          return res.data;
        })
      ).pipe(
        shareReplay(1), // 多播
        map((cutKeywords) => _.cloneDeep(cutKeywords))
      );
    }
    return this.cutKeywords$;
  },

  getCutKeywords(q: string, enforceNew?: boolean): Promise<string[]> {
    if (!q) {
      return Promise.resolve([]);
    }
    return this.requestCutKeywordsObservable(q, enforceNew).toPromise();
  },

  //#endregion

  //#region 计算 sn 单元格宽度
  getSnColWidth(projects: IProject[]): number {
    const SPACER_X = 34; // 水平方向的空白
    const SN_COL_MIN_WIDTH = 62; // _sn列（“序号”）的最小长度
    const NUMBER_WIDTH = 8.5; // 一个数字的宽度
    const DOT_WIDTH = 3.8; // 一个.的宽度
    const widths: number[] = [];
    _.forEach(projects, (project) => {
      const arr = project._sn.split('');
      const width = _.reduce(
        arr,
        (sum, item) => {
          return '.' === item ? sum + DOT_WIDTH : sum + NUMBER_WIDTH;
        },
        SPACER_X
      );
      widths.push(width);
    });
    return _.max([...widths, SN_COL_MIN_WIDTH]);
  },
  //#endregion

  //#region 配置页面顶部固定、内容滚动 
  setupStickyScroll(scrollEleSelector: string /* 滚动的元素 */, stickyTopEleSelector: string/* 顶部固定的元素 */, stickyTopEleAddClass: string /* 滚动后顶部固定元素追加的class */) {
    const scrollEle = document.querySelector(scrollEleSelector);
    scrollEle.addEventListener('scroll', (e) => {
      const scrollTop = (e.target as any).scrollTop;
      const stickyTopEles = document.querySelectorAll(stickyTopEleSelector);
      _.forEach(stickyTopEles, stickyTopEle => {
        if (stickyTopEle && scrollTop > 0) {
          stickyTopEle.classList.add(stickyTopEleAddClass);
        } else if (stickyTopEle && stickyTopEle.classList.contains(stickyTopEleAddClass)) {
          stickyTopEle.classList.remove(stickyTopEleAddClass);
        }
      })
    });
  }
  //#endregion
};
